import { useQuery } from "@apollo/client/react";
import { useMemo } from 'react'
import {
  type DashboardAnalytics,
  DashboardAnalyticsDocument,
  type DashboardAnalyticsInput,
  DashboardMembersDocument,
  DashboardTeamsDocument,
  DashboardWorkflowsDocument,
} from '#app/lib/data'
import type { DashboardUser, DashboardTeam, DashboardWorkflow } from '../data/types'

// Resolve once at module level — timezone does not change mid-session
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

interface UseDashboardDataParams {
  startDate: string
  endDate: string
  teamIds: string[]
  memberIds: string[]
}

export function useDashboardData({ startDate, endDate, teamIds, memberIds }: UseDashboardDataParams) {
  // The id lists are keyed BY VALUE: a caller rebuilding `['a']` every render
  // must not re-subscribe Apollo. Named so the dep array holds a variable the
  // rule can check rather than a call it cannot.
  const teamIdsKey = JSON.stringify(teamIds)
  const memberIdsKey = JSON.stringify(memberIds)

  // Memoize variables to prevent Apollo re-subscription loops (per CLAUDE.md)
  const analyticsVariables = useMemo(() => ({
    input: {
      startDate,
      endDate,
      teamIds: teamIds.length > 0 ? teamIds : undefined,
      memberIds: memberIds.length > 0 ? memberIds : undefined,
      timezone: TIMEZONE,
    } satisfies DashboardAnalyticsInput,
    // The id arrays' IDENTITY is deliberately not the key — `*IdsKey` is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [startDate, endDate, teamIdsKey, memberIdsKey])

  const analyticsResult = useQuery(DashboardAnalyticsDocument, {
    variables: analyticsVariables,
    fetchPolicy: 'cache-and-network',
  })

  const membersResult = useQuery(DashboardMembersDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const teamsResult = useQuery(DashboardTeamsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const workflowsResult = useQuery(DashboardWorkflowsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const analytics: DashboardAnalytics | null = analyticsResult.data?.dashboardAnalytics ?? null

  const members: DashboardUser[] = useMemo(
    () => membersResult.data?.users.edges.map(e => e.node) ?? [],
    [membersResult.data],
  )

  const teams: DashboardTeam[] = useMemo(
    () => teamsResult.data?.teams.edges.map(e => e.node) ?? [],
    [teamsResult.data],
  )

  const workflows: DashboardWorkflow[] = useMemo(
    () => workflowsResult.data?.workflows.edges.map(e => e.node) ?? [],
    [workflowsResult.data],
  )

  const loading =
    analyticsResult.loading ||
    membersResult.loading ||
    teamsResult.loading ||
    workflowsResult.loading

  const initialLoading = loading && !analyticsResult.data

  return {
    analytics,
    members,
    teams,
    workflows,
    loading,
    initialLoading,
    refetch: () => {
      analyticsResult.refetch()
      membersResult.refetch()
      teamsResult.refetch()
      workflowsResult.refetch()
    },
  }
}
